import { standingHardBalanceFailures } from "@capture/shared";
import { useBatchReconciliation, useReleaseBatch, useOverrideBalanceFailure, useClearBalanceReview } from "../hooks/useBatches";
import "../batches.css";

interface ReconciliationPanelProps {
  batchId: string;
  /**
   * R-264 5e-2 (Finding 1) — the batch's processing mode. "Release Anyway"
   * only applies to batch/hybrid mode; `releaseBatch` 400s for flow mode, so
   * the button is suppressed there (flow parks exit via Override/Clear only).
   */
  processingMode?: string;
  onRelease?: () => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value);
}

export function ReconciliationPanel({ batchId, processingMode, onRelease }: ReconciliationPanelProps) {
  const { reconciliation, isLoading, error, refresh, recheck } = useBatchReconciliation(batchId);
  const { release, isSubmitting } = useReleaseBatch();
  const { override } = useOverrideBalanceFailure();
  const { clear } = useClearBalanceReview();

  if (isLoading) {
    return (
      <div className="recon-panel">
        <div className="recon-title">Reconciliation</div>
        <div className="admin-loading"><div className="loading-spinner" /></div>
      </div>
    );
  }

  if (error || !reconciliation) {
    return (
      <div className="recon-panel">
        <div className="recon-title">Reconciliation</div>
        <div className="admin-error">{error ?? "No reconciliation data"}</div>
      </div>
    );
  }

  // R-264 5e-2 — standing hard failures: block entries that haven't been overridden.
  // Hard (block) failures cannot be overridden; only soft failures support override.
  const standingHard = standingHardBalanceFailures(reconciliation.balanceFailures ?? []);

  const hasDiscrepancy = reconciliation.discrepancy !== 0;

  // R-264 5e-2 (Finding 2) — a batch whose docs aren't all complete reconciles
  // as `waiting` (expectedTotal null, actualTotal 0). Render a distinct progress
  // state instead of a misleading "Fail / N/A / $0.00": suppress the Pass/Fail
  // badge, the currency cards, and Release Anyway. Balance rows + Re-check +
  // Clear still render — they remain meaningful while waiting.
  const isWaiting = reconciliation.waiting === true;

  // R-264 5e-2 (Finding 1) — "Release Anyway" is server-gated to batch/hybrid
  // mode (releaseBatch 400s otherwise). Suppress the button in flow mode so it
  // isn't a silent dead button; flow parks exit via Override/Clear only.
  const canReleaseAnyway = processingMode === "batch" || processingMode === "hybrid";

  // R-264 5e-2 (Finding 3) — `passed` only reflects control-total reconcile, not
  // the balance snapshot. A batch with clean totals but a standing hard balance
  // failure is still out of balance, so the badge must read Fail. The balance
  // rows already explain why; reuse the existing fail badge.
  const badgePassed = reconciliation.passed && standingHard.length === 0;

  const handleReleaseAnyway = async () => {
    await release(batchId);
    onRelease?.();
  };

  const handleRecheck = async () => {
    await recheck();
  };

  const handleOverride = async (ruleId: string) => {
    await override(batchId, ruleId);
    refresh();
  };

  const handleClearBalanceReview = async () => {
    await clear(batchId);
    refresh();
  };

  return (
    <div className="recon-panel" data-testid="reconciliation-panel">
      <div className="recon-title">Reconciliation</div>

      <div className="recon-status">
        {isWaiting ? (
          <span
            className="recon-status-badge recon-status-badge--waiting"
            data-testid="recon-waiting"
          >
            Waiting — {reconciliation.completedDocuments}/{reconciliation.totalDocuments} documents complete
          </span>
        ) : (
          <span
            className={`recon-status-badge ${badgePassed ? "recon-status-badge--pass" : "recon-status-badge--fail"}`}
          >
            {badgePassed ? "Pass" : "Fail"}
          </span>
        )}
      </div>

      {!isWaiting && (
        <div className="recon-totals">
          <div className="recon-total-card">
            <div className="recon-total-label">Expected</div>
            <div className="recon-total-value" data-testid="recon-expected">
              {reconciliation.expectedTotal !== null
                ? formatCurrency(reconciliation.expectedTotal)
                : "N/A"}
            </div>
          </div>
          <div className="recon-total-card">
            <div className="recon-total-label">Actual</div>
            <div className="recon-total-value" data-testid="recon-actual">
              {formatCurrency(reconciliation.actualTotal)}
            </div>
          </div>
          <div className="recon-total-card">
            <div className="recon-total-label">Discrepancy</div>
            <div
              className={`recon-total-value ${hasDiscrepancy ? "recon-total-value--discrepancy" : "recon-total-value--match"}`}
              data-testid="recon-discrepancy"
            >
              {formatCurrency(Math.abs(reconciliation.discrepancy))}
            </div>
          </div>
        </div>
      )}

      {reconciliation.failures.length > 0 && (
        <div className="recon-failures">
          <div className="recon-failures-title">
            {reconciliation.failures.length} failure{reconciliation.failures.length !== 1 ? "s" : ""}
          </div>
          {reconciliation.failures.map((f, i) => (
            <div key={i} className="recon-failure-item">
              <span className="recon-failure-type">{f.type}</span>
              <span className="recon-failure-message">{f.message}</span>
              {f.details && <div className="recon-failure-details">{f.details}</div>}
            </div>
          ))}
        </div>
      )}

      {/* R-264 5e-2 — balance failure rows: rule-engine failures from the balance
          gate. Hard (block) failures cannot be overridden; soft failures support
          per-rule override with an audit trail. */}
      {(reconciliation.balanceFailures ?? []).length > 0 && (
        <div className="recon-failures" data-testid="recon-balance-failures">
          <div className="recon-failures-title">Balance</div>
          {(reconciliation.balanceFailures ?? []).map((f) => (
            <div key={f.ruleId} className="recon-failure-item">
              <span className="recon-failure-type">{f.overrideBehavior === "block" ? "hard" : "soft"}</span>
              <span className="recon-failure-message">
                {f.ruleName}: declared {String(f.declared ?? "—")}, actual {String(f.actual ?? "—")}
              </span>
              {f.overridden ? (
                <span className="recon-overridden">Overridden</span>
              ) : f.overrideBehavior !== "block" ? (
                <button
                  type="button"
                  className="batch-action-btn"
                  disabled={isSubmitting}
                  aria-label={`Override ${f.ruleName}`}
                  onClick={() => void handleOverride(f.ruleId)}
                >
                  Override
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      <div className="recon-actions">
        {/* Re-check: always shown; runs the persisting recheck endpoint and
            updates the panel in place */}
        <button
          type="button"
          className="batch-action-btn"
          disabled={isLoading}
          onClick={() => void handleRecheck()}
        >
          Re-check
        </button>

        {/* Release Anyway: disabled while any un-overridden hard (block) failure
            stands — the server also refuses via releaseBatch; gate mirrors that. */}
        {!isWaiting && !reconciliation.passed && canReleaseAnyway && (
          <button
            type="button"
            className="batch-action-btn batch-action-btn--primary"
            onClick={() => void handleReleaseAnyway()}
            disabled={isSubmitting || standingHard.length > 0}
            title={standingHard.length > 0 ? "Blocked: un-overridden hard balance failures must pass or be resolved before releasing" : undefined}
          >
            {isSubmitting ? "Releasing..." : "Release Anyway"}
          </button>
        )}

        {/* Clear balance review: the I-3 manual exit from an advisory balance
            park. Only shown when status is balance_review AND no hard failure
            stands un-overridden (the API 409s otherwise). */}
        {reconciliation.status === "balance_review" && standingHard.length === 0 && (
          <button
            type="button"
            className="batch-action-btn"
            disabled={isSubmitting}
            onClick={() => void handleClearBalanceReview()}
          >
            Clear balance review
          </button>
        )}
      </div>
    </div>
  );
}
